import type { Lang } from '../types';

/**
 * Wording for security deposits.
 *
 * <p>Two things this copy has to get right, because both cause support messages otherwise.
 * <b>A hold is not a charge</b> — but the money genuinely is unavailable to the guest, so the
 * wording can never say "nothing happens"; it says the amount is set aside. And <b>releasing is
 * instant at Stripe and slow at the bank</b>, so every release message says so outright rather
 * than leaving the guest to conclude their money vanished.
 */
export interface DepositText {
  // Manager — settings
  settingsTitle: string;
  settingsIntro: string;
  enabledLabel: string;
  enabledHint: string;
  amountLabel: string;
  amountHint: string;
  leadLabel: string;
  leadHint: string;
  decisionLabel: string;
  decisionHint: string;
  autoLabel: string;
  autoOn: string;
  autoOnHint: string;
  autoOff: string;
  autoOffHint: string;
  noStripe: string;
  noStripeFix: string;
  needAmount: string;
  saved: string;
  // Manager — the queue
  queueTitle: string;
  queueEmpty: string;
  queueIntro: string;
  needsDecision: string;
  noDeposit: string;
  heldOk: string;
  toArrival: string;
  holdExpires: string;
  inStay: string;
  overdue: string;
  // Manager — the decision
  decideTitle: string;
  heldSince: string;
  notCharged: string;
  deadlineWarn: string;
  releaseCta: string;
  releaseDesc: string;
  keepCta: string;
  keepDesc: string;
  keepHowMuch: string;
  guestGetsBack: string;
  reasonLabel: string;
  noteLabel: string;
  notePlaceholder: string;
  guestSees: string;
  confirmRelease: string;
  confirmReleaseWhy: string;
  confirmKeep: string;
  confirmKeepWhy: string;
  confirmKeepAllWhy: string;
  back: string;
  yesRelease: string;
  yesKeep: string;
  waiveCta: string;
  waiveWhy: string;
  waived: string;
  // Outcomes as the manager sees them
  outReleased: string;
  outClaimed: string;
  outExpired: string;
  // Claim reasons — manager's picker
  mDamage: string;
  mMissing: string;
  mCleaning: string;
  mSmoking: string;
  mGuests: string;
  mLate: string;
  mOther: string;
  // Claim reasons — what the guest reads
  gDamage: string;
  gMissing: string;
  gCleaning: string;
  gSmoking: string;
  gGuests: string;
  gLate: string;
  gOther: string;
  // Guest — the card step and the key block
  guestNeedCardTitle: string;
  guestNeedCardBody: string;
  guestAddCard: string;
  guestFailedTitle: string;
  guestFailedBody: string;
  guestUpdateCard: string;
  // Guest — the status page after check-out
  guestHeldTitle: string;
  guestHeldBody: string;
  guestReleasedTitle: string;
  guestReleasedBody: string;
  guestKeptTitle: string;
  guestKeptBody: string;
  guestBankDelay: string;
  guestAskHost: string;
  actionError: string;
}

export const depositText: Record<Lang, DepositText> = {
  en: {
    settingsTitle: 'Security deposit',
    settingsIntro: 'Reserve an amount on the guest’s card before they arrive. Nothing is charged unless you keep part of it.',
    enabledLabel: 'Collect a deposit',
    enabledHint: 'Off by default. Guests are told the amount before they book.',
    amountLabel: 'Amount',
    amountHint: 'Flat per stay — a deposit covers the apartment, not the number of nights.',
    leadLabel: 'Reserve it',
    leadHint: 'days before check-in. Late enough that the hold lasts the stay, early enough that a declined card is an email rather than a locked door.',
    decisionLabel: 'You have',
    decisionHint: 'days after check-out to decide. After about a week the hold lapses on its own and the money returns.',
    autoLabel: 'If you don’t decide',
    autoOn: 'Release it automatically',
    autoOnHint: 'We let it go on the last day and email you both.',
    autoOff: 'Just remind me',
    autoOffHint: 'We chase you. If you still don’t act, the hold lapses by itself.',
    noStripe: 'This apartment has no Stripe account that can take a charge.',
    noStripeFix: 'Set one up under Payments first.',
    needAmount: 'Set an amount before switching deposits on.',
    saved: 'Saved',
    queueTitle: 'Deposits',
    queueEmpty: 'Nothing needs you.',
    queueIntro: 'Held money waiting on a decision, and arrivals that aren’t covered yet.',
    needsDecision: 'Decide',
    noDeposit: 'No deposit',
    heldOk: 'Held',
    toArrival: 'to arrival',
    holdExpires: 'hold expires',
    inStay: 'in stay',
    overdue: 'overdue',
    decideTitle: 'Security deposit',
    heldSince: 'held since {date}',
    notCharged: 'not charged',
    deadlineWarn: 'Decide by {date}. After that the hold lapses on its own and the full amount returns to the guest. There is no way to extend it.',
    releaseCta: 'Release everything',
    releaseDesc: 'The flat is fine. The guest keeps all of it — nothing is charged, ever.',
    keepCta: 'Keep part of it',
    keepDesc: 'Charge what you need to cover. The rest is released automatically.',
    keepHowMuch: 'How much to keep',
    guestGetsBack: 'Guest gets back',
    reasonLabel: 'Reason — the guest sees this in their own language',
    noteLabel: 'Your own note — optional, never sent to the guest',
    notePlaceholder: 'Glass coffee table cracked, found during turnover.',
    guestSees: 'What the guest receives',
    confirmRelease: 'Release the whole deposit?',
    confirmReleaseWhy: '{amount} goes back and the hold is gone for good. If you find something afterwards there is nothing left to charge — a released hold cannot be placed again.',
    confirmKeep: 'Charge {amount} to the guest’s card?',
    confirmKeepWhy: '{rest} is released back to them at the same time. A capture cannot be undone.',
    confirmKeepAllWhy: 'Nothing is released — they get none of the deposit back. A capture cannot be undone.',
    back: 'Go back',
    yesRelease: 'Yes, release it',
    yesKeep: 'Yes, charge {amount}',
    waiveCta: 'Let them in without a deposit',
    waiveWhy: 'Use this when you know something we don’t — they paid cash, or you trust them. The stay stays flagged as uncovered.',
    waived: 'You let this guest in without a deposit.',
    outReleased: 'Released in full on {date}. Nothing was charged.',
    outClaimed: '{amount} kept on {date}. {rest} was returned.',
    outExpired: 'The hold lapsed on {date} with no decision. The guest got everything back.',
    mDamage: 'Damage',
    mMissing: 'Something missing',
    mCleaning: 'Extra cleaning',
    mSmoking: 'Smoking',
    mGuests: 'Extra guests',
    mLate: 'Late check-out',
    mOther: 'Other',
    gDamage: 'Damage to the property or its contents.',
    gMissing: 'Something was missing from the apartment.',
    gCleaning: 'Cleaning beyond what a normal stay needs.',
    gSmoking: 'Smoking in a non-smoking apartment.',
    gGuests: 'More guests stayed than were booked.',
    gLate: 'The apartment was used past check-out without arranging it.',
    gOther: 'Your host will explain the reason separately.',
    guestNeedCardTitle: 'A {amount} deposit is needed for this stay',
    guestNeedCardBody: 'It is reserved on your card before you arrive and released after you check out. Nothing is charged unless something is damaged.',
    guestAddCard: 'Add card',
    guestFailedTitle: 'We couldn’t reserve the {amount} deposit',
    guestFailedBody: 'Your entry code appears here once it’s reserved. Nothing has been charged — the amount is held during your stay and released after check-out.',
    guestUpdateCard: 'Update card',
    guestHeldTitle: 'Deposit held',
    guestHeldBody: 'Your host is checking the apartment. The deposit is released after that — by {date} at the latest. Nothing has been charged.',
    guestReleasedTitle: 'Released',
    guestReleasedBody: 'Released on {date}. Nothing was charged.',
    guestKeptTitle: 'Partly kept',
    guestKeptBody: 'Your host kept {amount} of the {total} deposit. {rest} was released back to your card.',
    guestBankDelay: 'Your bank may take a few more days to remove it from your statement.',
    guestAskHost: 'Questions about this go to your host.',
    actionError: 'That did not work. Please reload and try again.',
  },

  cs: {
    settingsTitle: 'Vratná kauce',
    settingsIntro: 'Rezervujte částku na kartě hosta ještě před příjezdem. Nic se nestrhne, pokud si část neponecháte.',
    enabledLabel: 'Vybírat kauci',
    enabledHint: 'Ve výchozím stavu vypnuto. Host se o částce dozví před rezervací.',
    amountLabel: 'Částka',
    amountHint: 'Jednotná za pobyt — kauce kryje byt, ne počet nocí.',
    leadLabel: 'Rezervovat',
    leadHint: 'dní před příjezdem. Dost pozdě, aby blokace vydržela pobyt, a dost brzy, aby odmítnutá karta byl e-mail a ne zamčené dveře.',
    decisionLabel: 'Máte',
    decisionHint: 'dní po odjezdu na rozhodnutí. Zhruba po týdnu blokace sama vyprší a peníze se vrátí.',
    autoLabel: 'Když nerozhodnete',
    autoOn: 'Automaticky uvolnit',
    autoOnHint: 'Poslední den ji pustíme a oběma pošleme e-mail.',
    autoOff: 'Jen mi připomeňte',
    autoOffHint: 'Budeme vás urgovat. Když ani pak nezareagujete, blokace vyprší sama.',
    noStripe: 'Tento byt nemá Stripe účet, který by mohl přijmout platbu.',
    noStripeFix: 'Nejprve ho nastavte v sekci Platby.',
    needAmount: 'Před zapnutím kauce nastavte částku.',
    saved: 'Uloženo',
    queueTitle: 'Kauce',
    queueEmpty: 'Nic nečeká.',
    queueIntro: 'Blokované peníze čekající na rozhodnutí a příjezdy bez kauce.',
    needsDecision: 'Rozhodnout',
    noDeposit: 'Bez kauce',
    heldOk: 'Blokováno',
    toArrival: 'do příjezdu',
    holdExpires: 'blokace vyprší',
    inStay: 'probíhá pobyt',
    overdue: 'po termínu',
    decideTitle: 'Vratná kauce',
    heldSince: 'blokováno od {date}',
    notCharged: 'nestrženo',
    deadlineWarn: 'Rozhodněte do {date}. Poté blokace sama vyprší a celá částka se vrátí hostovi. Prodloužit ji nelze.',
    releaseCta: 'Uvolnit vše',
    releaseDesc: 'Byt je v pořádku. Host dostane vše zpět — nic se nestrhne.',
    keepCta: 'Ponechat si část',
    keepDesc: 'Strhněte, co potřebujete pokrýt. Zbytek se uvolní automaticky.',
    keepHowMuch: 'Kolik si ponechat',
    guestGetsBack: 'Host dostane zpět',
    reasonLabel: 'Důvod — host ho uvidí ve svém jazyce',
    noteLabel: 'Vaše poznámka — nepovinná, hostovi se neposílá',
    notePlaceholder: 'Prasklý skleněný konferenční stolek, nalezeno při úklidu.',
    guestSees: 'Co host dostane',
    confirmRelease: 'Uvolnit celou kauci?',
    confirmReleaseWhy: '{amount} se vrátí hostovi a blokace nenávratně zmizí. Když něco najdete později, nebude už z čeho strhnout — blokaci nelze vytvořit znovu.',
    confirmKeep: 'Strhnout {amount} z karty hosta?',
    confirmKeepWhy: '{rest} se mu zároveň uvolní zpět. Stržení nelze vzít zpět.',
    confirmKeepAllWhy: 'Nic se neuvolní — host nedostane zpět nic. Stržení nelze vzít zpět.',
    back: 'Zpět',
    yesRelease: 'Ano, uvolnit',
    yesKeep: 'Ano, strhnout {amount}',
    waiveCta: 'Pustit dovnitř bez kauce',
    waiveWhy: 'Použijte, když víte něco, co my ne — zaplatil hotově nebo mu věříte. Pobyt zůstane označen jako nekrytý.',
    waived: 'Tohoto hosta jste pustili dovnitř bez kauce.',
    outReleased: 'Uvolněno v plné výši {date}. Nic se nestrhlo.',
    outClaimed: '{amount} ponecháno {date}. {rest} vráceno.',
    outExpired: 'Blokace vypršela {date} bez rozhodnutí. Host dostal vše zpět.',
    mDamage: 'Poškození',
    mMissing: 'Něco chybí',
    mCleaning: 'Úklid navíc',
    mSmoking: 'Kouření',
    mGuests: 'Hosté navíc',
    mLate: 'Pozdní odchod',
    mOther: 'Jiné',
    gDamage: 'Poškození bytu nebo jeho vybavení.',
    gMissing: 'Z bytu něco chybí.',
    gCleaning: 'Úklid nad rámec běžného pobytu.',
    gSmoking: 'Kouření v nekuřáckém bytě.',
    gGuests: 'Ubytovalo se více hostů, než bylo rezervováno.',
    gLate: 'Byt byl užíván po čase odjezdu bez domluvy.',
    gOther: 'Důvod vám hostitel vysvětlí zvlášť.',
    guestNeedCardTitle: 'Na tento pobyt je potřeba kauce {amount}',
    guestNeedCardBody: 'Rezervuje se na vaší kartě před příjezdem a uvolní se po odjezdu. Nic se nestrhne, pokud se nic nepoškodí.',
    guestAddCard: 'Přidat kartu',
    guestFailedTitle: 'Nepodařilo se rezervovat kauci {amount}',
    guestFailedBody: 'Kód ke vstupu se zde objeví, jakmile bude kauce rezervována. Nic se nestrhlo — částka je blokovaná po dobu pobytu a po odjezdu se uvolní.',
    guestUpdateCard: 'Upravit kartu',
    guestHeldTitle: 'Kauce blokována',
    guestHeldBody: 'Hostitel kontroluje byt. Poté se kauce uvolní — nejpozději {date}. Nic se nestrhlo.',
    guestReleasedTitle: 'Uvolněno',
    guestReleasedBody: 'Uvolněno {date}. Nic se nestrhlo.',
    guestKeptTitle: 'Část ponechána',
    guestKeptBody: 'Hostitel si ponechal {amount} z kauce {total}. {rest} bylo vráceno na vaši kartu.',
    guestBankDelay: 'Bance může trvat několik dní, než částku z výpisu odstraní.',
    guestAskHost: 'S dotazy se obracejte na hostitele.',
    actionError: 'Nepodařilo se. Načtěte stránku znovu a zkuste to prosím ještě jednou.',
  },

  vi: {
    settingsTitle: 'Tiền đặt cọc',
    settingsIntro: 'Giữ một khoản trên thẻ của khách trước khi họ đến. Không trừ tiền trừ khi bạn giữ lại một phần.',
    enabledLabel: 'Thu tiền cọc',
    enabledHint: 'Mặc định tắt. Khách được báo số tiền trước khi đặt.',
    amountLabel: 'Số tiền',
    amountHint: 'Cố định cho cả kỳ nghỉ — cọc bảo đảm cho căn hộ, không theo số đêm.',
    leadLabel: 'Giữ tiền',
    leadHint: 'ngày trước khi nhận phòng. Đủ muộn để khoản giữ kéo dài hết kỳ nghỉ, đủ sớm để thẻ bị từ chối chỉ là một email.',
    decisionLabel: 'Bạn có',
    decisionHint: 'ngày sau khi trả phòng để quyết định. Sau khoảng một tuần khoản giữ tự hết hạn và tiền được trả lại.',
    autoLabel: 'Nếu bạn không quyết định',
    autoOn: 'Tự động trả lại',
    autoOnHint: 'Chúng tôi trả vào ngày cuối và gửi email cho cả hai.',
    autoOff: 'Chỉ nhắc tôi',
    autoOffHint: 'Chúng tôi sẽ nhắc. Nếu vẫn không xử lý, khoản giữ tự hết hạn.',
    noStripe: 'Căn hộ này chưa có tài khoản Stripe nhận được thanh toán.',
    noStripeFix: 'Hãy thiết lập trong mục Thanh toán trước.',
    needAmount: 'Đặt số tiền trước khi bật tiền cọc.',
    saved: 'Đã lưu',
    queueTitle: 'Tiền cọc',
    queueEmpty: 'Không có gì cần bạn.',
    queueIntro: 'Tiền đang giữ chờ quyết định, và những lượt đến chưa có cọc.',
    needsDecision: 'Quyết định',
    noDeposit: 'Chưa có cọc',
    heldOk: 'Đang giữ',
    toArrival: 'đến lúc nhận phòng',
    holdExpires: 'khoản giữ hết hạn',
    inStay: 'đang ở',
    overdue: 'quá hạn',
    decideTitle: 'Tiền đặt cọc',
    heldSince: 'giữ từ {date}',
    notCharged: 'chưa trừ tiền',
    deadlineWarn: 'Quyết định trước {date}. Sau đó khoản giữ tự hết hạn và toàn bộ tiền trả lại khách. Không thể gia hạn.',
    releaseCta: 'Trả lại tất cả',
    releaseDesc: 'Căn hộ ổn. Khách nhận lại toàn bộ — không trừ đồng nào.',
    keepCta: 'Giữ lại một phần',
    keepDesc: 'Trừ số bạn cần bù. Phần còn lại tự động được trả.',
    keepHowMuch: 'Giữ lại bao nhiêu',
    guestGetsBack: 'Khách nhận lại',
    reasonLabel: 'Lý do — khách đọc bằng ngôn ngữ của họ',
    noteLabel: 'Ghi chú của bạn — không bắt buộc, không gửi cho khách',
    notePlaceholder: 'Bàn kính bị nứt, phát hiện khi dọn phòng.',
    guestSees: 'Khách sẽ nhận được',
    confirmRelease: 'Trả lại toàn bộ tiền cọc?',
    confirmReleaseWhy: '{amount} trả lại khách và khoản giữ mất hẳn. Nếu sau đó phát hiện gì thì không còn gì để trừ — không thể giữ lại lần nữa.',
    confirmKeep: 'Trừ {amount} từ thẻ của khách?',
    confirmKeepWhy: '{rest} được trả lại cho họ cùng lúc. Đã trừ thì không hoàn tác được.',
    confirmKeepAllWhy: 'Không trả lại gì — khách không nhận lại đồng nào. Đã trừ thì không hoàn tác được.',
    back: 'Quay lại',
    yesRelease: 'Vâng, trả lại',
    yesKeep: 'Vâng, trừ {amount}',
    waiveCta: 'Cho vào mà không cần cọc',
    waiveWhy: 'Dùng khi bạn biết điều chúng tôi không biết — họ trả tiền mặt, hoặc bạn tin họ. Kỳ nghỉ vẫn được đánh dấu là chưa có cọc.',
    waived: 'Bạn đã cho khách này vào mà không có tiền cọc.',
    outReleased: 'Đã trả lại toàn bộ ngày {date}. Không trừ đồng nào.',
    outClaimed: 'Giữ {amount} ngày {date}. {rest} đã được trả lại.',
    outExpired: 'Khoản giữ hết hạn ngày {date} mà không có quyết định. Khách nhận lại tất cả.',
    mDamage: 'Hư hỏng',
    mMissing: 'Thiếu đồ',
    mCleaning: 'Dọn thêm',
    mSmoking: 'Hút thuốc',
    mGuests: 'Thêm khách',
    mLate: 'Trả phòng muộn',
    mOther: 'Khác',
    gDamage: 'Hư hỏng căn hộ hoặc đồ đạc bên trong.',
    gMissing: 'Có đồ trong căn hộ bị thiếu.',
    gCleaning: 'Dọn dẹp vượt mức một kỳ nghỉ bình thường.',
    gSmoking: 'Hút thuốc trong căn hộ cấm hút thuốc.',
    gGuests: 'Số khách ở nhiều hơn số đã đặt.',
    gLate: 'Sử dụng căn hộ quá giờ trả phòng mà không báo trước.',
    gOther: 'Chủ nhà sẽ giải thích lý do riêng.',
    guestNeedCardTitle: 'Kỳ nghỉ này cần đặt cọc {amount}',
    guestNeedCardBody: 'Khoản này được giữ trên thẻ trước khi bạn đến và trả lại sau khi trả phòng. Không trừ tiền trừ khi có hư hỏng.',
    guestAddCard: 'Thêm thẻ',
    guestFailedTitle: 'Không giữ được khoản cọc {amount}',
    guestFailedBody: 'Mã vào cửa sẽ hiện ở đây khi khoản cọc được giữ. Chưa trừ đồng nào — số tiền chỉ được giữ trong kỳ nghỉ và trả lại sau khi trả phòng.',
    guestUpdateCard: 'Cập nhật thẻ',
    guestHeldTitle: 'Đang giữ cọc',
    guestHeldBody: 'Chủ nhà đang kiểm tra căn hộ. Sau đó khoản cọc sẽ được trả — chậm nhất là {date}. Chưa trừ đồng nào.',
    guestReleasedTitle: 'Đã trả lại',
    guestReleasedBody: 'Đã trả lại ngày {date}. Không trừ đồng nào.',
    guestKeptTitle: 'Giữ lại một phần',
    guestKeptBody: 'Chủ nhà giữ {amount} trong khoản cọc {total}. {rest} đã được trả lại thẻ của bạn.',
    guestBankDelay: 'Ngân hàng có thể mất vài ngày nữa để xóa khoản này khỏi sao kê.',
    guestAskHost: 'Mọi thắc mắc xin liên hệ chủ nhà.',
    actionError: 'Không thành công. Vui lòng tải lại trang và thử lại.',
  },

  uk: {
    settingsTitle: 'Заставa',
    settingsIntro: 'Зарезервуйте суму на картці гостя до його приїзду. Нічого не списується, доки ви не залишите частину собі.',
    enabledLabel: 'Брати заставу',
    enabledHint: 'Типово вимкнено. Гість дізнається суму до бронювання.',
    amountLabel: 'Сума',
    amountHint: 'Фіксована за проживання — застава покриває квартиру, а не кількість ночей.',
    leadLabel: 'Резервувати',
    leadHint: 'днів до заселення. Досить пізно, щоб резерв протримався все проживання, і досить рано, щоб відхилена картка була листом, а не замкненими дверима.',
    decisionLabel: 'У вас є',
    decisionHint: 'днів після виселення на рішення. Приблизно за тиждень резерв спливає сам і гроші повертаються.',
    autoLabel: 'Якщо ви не вирішите',
    autoOn: 'Повернути автоматично',
    autoOnHint: 'Останнього дня відпускаємо і надсилаємо листи обом.',
    autoOff: 'Просто нагадайте мені',
    autoOffHint: 'Ми нагадуватимемо. Якщо й тоді нічого — резерв спливе сам.',
    noStripe: 'У цієї квартири немає рахунку Stripe, здатного прийняти оплату.',
    noStripeFix: 'Спершу налаштуйте його в розділі «Платежі».',
    needAmount: 'Вкажіть суму, перш ніж вмикати заставу.',
    saved: 'Збережено',
    queueTitle: 'Застави',
    queueEmpty: 'Нічого не потребує вас.',
    queueIntro: 'Зарезервовані гроші, що чекають рішення, і заїзди без застави.',
    needsDecision: 'Вирішити',
    noDeposit: 'Без застави',
    heldOk: 'Зарезервовано',
    toArrival: 'до заїзду',
    holdExpires: 'резерв спливає',
    inStay: 'триває проживання',
    overdue: 'прострочено',
    decideTitle: 'Застава',
    heldSince: 'зарезервовано з {date}',
    notCharged: 'не списано',
    deadlineWarn: 'Вирішіть до {date}. Після цього резерв спливе сам і вся сума повернеться гостю. Продовжити неможливо.',
    releaseCta: 'Повернути все',
    releaseDesc: 'З квартирою все гаразд. Гість отримує все назад — нічого не списується.',
    keepCta: 'Залишити частину',
    keepDesc: 'Спишіть стільки, скільки треба покрити. Решта повертається автоматично.',
    keepHowMuch: 'Скільки залишити',
    guestGetsBack: 'Гість отримає назад',
    reasonLabel: 'Причина — гість прочитає її своєю мовою',
    noteLabel: 'Ваша нотатка — необов’язкова, гостю не надсилається',
    notePlaceholder: 'Тріснув скляний журнальний столик, виявлено під час прибирання.',
    guestSees: 'Що отримає гість',
    confirmRelease: 'Повернути всю заставу?',
    confirmReleaseWhy: '{amount} повертається гостю, а резерв зникає назавжди. Якщо щось знайдете потім — списувати вже не буде з чого, резерв не можна створити вдруге.',
    confirmKeep: 'Списати {amount} з картки гостя?',
    confirmKeepWhy: '{rest} повертається йому водночас. Списання не можна скасувати.',
    confirmKeepAllWhy: 'Нічого не повертається — гість не отримає назад нічого. Списання не можна скасувати.',
    back: 'Назад',
    yesRelease: 'Так, повернути',
    yesKeep: 'Так, списати {amount}',
    waiveCta: 'Впустити без застави',
    waiveWhy: 'Використайте, коли знаєте те, чого не знаємо ми — заплатив готівкою або ви йому довіряєте. Проживання лишиться позначеним як незабезпечене.',
    waived: 'Ви впустили цього гостя без застави.',
    outReleased: 'Повернуто повністю {date}. Нічого не списано.',
    outClaimed: '{amount} залишено {date}. {rest} повернуто.',
    outExpired: 'Резерв сплив {date} без рішення. Гість отримав усе назад.',
    mDamage: 'Пошкодження',
    mMissing: 'Щось зникло',
    mCleaning: 'Додаткове прибирання',
    mSmoking: 'Куріння',
    mGuests: 'Зайві гості',
    mLate: 'Пізнє виселення',
    mOther: 'Інше',
    gDamage: 'Пошкодження житла або його обладнання.',
    gMissing: 'З квартири щось зникло.',
    gCleaning: 'Прибирання понад звичайне після проживання.',
    gSmoking: 'Куріння в квартирі, де це заборонено.',
    gGuests: 'Проживало більше гостей, ніж було заброньовано.',
    gLate: 'Квартиру використовували після виселення без домовленості.',
    gOther: 'Господар пояснить причину окремо.',
    guestNeedCardTitle: 'Для цього проживання потрібна застава {amount}',
    guestNeedCardBody: 'Вона резервується на вашій картці до приїзду і звільняється після виселення. Нічого не списується, якщо нічого не пошкоджено.',
    guestAddCard: 'Додати картку',
    guestFailedTitle: 'Не вдалося зарезервувати заставу {amount}',
    guestFailedBody: 'Код для входу з’явиться тут, щойно заставу буде зарезервовано. Нічого не списано — сума лише блокується на час проживання і звільняється після виселення.',
    guestUpdateCard: 'Оновити картку',
    guestHeldTitle: 'Заставу зарезервовано',
    guestHeldBody: 'Господар перевіряє квартиру. Після цього заставу буде звільнено — щонайпізніше {date}. Нічого не списано.',
    guestReleasedTitle: 'Звільнено',
    guestReleasedBody: 'Звільнено {date}. Нічого не списано.',
    guestKeptTitle: 'Частину залишено',
    guestKeptBody: 'Господар залишив {amount} із застави {total}. {rest} повернуто на вашу картку.',
    guestBankDelay: 'Банку може знадобитися ще кілька днів, щоб прибрати суму з виписки.',
    guestAskHost: 'З питаннями звертайтеся до господаря.',
    actionError: 'Не вдалося. Перезавантажте сторінку і спробуйте ще раз.',
  },
};
